/**
 * Shared Personas View Component
 *
 * Displays sandbox personas shared with the current researcher.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  Psychology as TherapistIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Star as FullIcon
} from '@mui/icons-material';
import * as researcherApi from '../../services/researcherApi';

const SharedPersonasView = ({ onSelectPersona }) => {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSharedPersonas();
  }, []);

  const loadSharedPersonas = async () => {
    try {
      setLoading(true);
      const data = await researcherApi.getSharedWithMe();
      setPersonas(data);
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load shared personas');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'view':
        return <ViewIcon fontSize="small" />;
      case 'edit':
        return <EditIcon fontSize="small" />;
      case 'full':
        return <FullIcon fontSize="small" />;
      default:
        return <ViewIcon fontSize="small" />;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'view':
        return 'default';
      case 'edit':
        return 'primary';
      case 'full':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getMyPermission = (persona, currentUserId) => {
    const share = persona.sharedWith?.find(s => s.userId === currentUserId);
    return share?.permission || 'view';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (personas.length === 0) {
    return (
      <Alert severity="info">
        No personas have been shared with you yet. Ask other researchers to share their sandbox personas.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Personas Shared With Me ({personas.length})
      </Typography>

      <Grid container spacing={2}>
        {personas.map((persona) => (
          <Grid item xs={12} md={6} lg={4} key={persona._id}>
            <Card
              sx={{
                cursor: onSelectPersona ? 'pointer' : 'default',
                '&:hover': onSelectPersona ? {
                  boxShadow: 4,
                  borderColor: 'primary.main'
                } : {}
              }}
              onClick={() => onSelectPersona && onSelectPersona(persona)}
            >
              <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Avatar
                    sx={{
                      bgcolor: persona.personaType === 'client' ? 'info.light' : 'success.light'
                    }}
                  >
                    {persona.personaType === 'client' ? (
                      <PersonIcon />
                    ) : (
                      <TherapistIcon />
                    )}
                  </Avatar>

                  <Box flex={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle1" fontWeight="medium">
                        {persona.name}
                      </Typography>
                      <Tooltip title={`Permission: ${persona.sharedWith?.[0]?.permission || 'view'}`}>
                        <Chip
                          icon={getPermissionIcon(persona.sharedWith?.[0]?.permission)}
                          label={persona.sharedWith?.[0]?.permission || 'view'}
                          size="small"
                          color={getPermissionColor(persona.sharedWith?.[0]?.permission)}
                        />
                      </Tooltip>
                    </Box>

                    <Typography variant="caption" color="textSecondary" display="block">
                      Key: {persona.key}
                    </Typography>

                    <Chip
                      label={persona.personaType}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, mb: 1 }}
                    />

                    {persona.description && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          mt: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {persona.description}
                      </Typography>
                    )}

                    {persona.createdBy && (
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                        Shared by: {persona.createdBy.name || persona.createdBy.email}
                      </Typography>
                    )}

                    {/* Metadata tags */}
                    {persona.metadata?.conditions?.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                        {persona.metadata.conditions.slice(0, 3).map((condition, idx) => (
                          <Chip
                            key={idx}
                            label={condition}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                        {persona.metadata.conditions.length > 3 && (
                          <Chip
                            label={`+${persona.metadata.conditions.length - 3}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SharedPersonasView;
